import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, Download, FileText, Loader2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentiFirmaPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [luogo, setLuogo] = useState("");
  const [signing, setSigning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const sigRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/documenti-firma/${token}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link non valido o scaduto");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pdfUrl = `${API_URL}/api/documenti-firma/${token}/modello`;

  const handleFirma = async () => {
    if (!luogo.trim()) { toast.error("Inserisci il luogo"); return; }
    if (sigRef.current?.isEmpty()) { toast.error("Disegna la firma"); return; }
    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/documenti-firma/${token}/firma`, {
        firma_base64: sigRef.current.toDataURL("image/png"),
        luogo: luogo.trim(),
      });
      toast.success("Documento firmato!");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore firma");
    } finally {
      setSigning(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API_URL}/api/documenti-firma/${token}/upload-firmato`, fd);
      toast.success("Documento caricato!");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center">
        <div>
          <FileText className="mx-auto h-12 w-12 text-slate-300" />
          <h1 className="mt-4 text-lg font-semibold text-slate-800">Link non valido</h1>
          <p className="mt-2 text-sm text-slate-500">Il link potrebbe essere scaduto o già utilizzato.</p>
        </div>
      </div>
    );
  }

  const firmato = data.stato === "firmato";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white px-6 py-4">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#00A86B]">Affidya — Portale documenti</p>
            <h1 className="text-lg font-bold text-slate-900">{data.modulo_titolo}</h1>
            <p className="text-sm text-slate-500">Ospite: {data.ospite_nome}</p>
          </div>
          {firmato ? (
            <Badge className="bg-emerald-100 text-emerald-800">Firmato {data.firmato_at ? new Date(data.firmato_at).toLocaleString("it-IT") : ""}</Badge>
          ) : (
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">In attesa</Badge>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl p-6 space-y-6">
        {firmato ? (
          <div className="rounded-xl border bg-white p-8 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <h2 className="mt-4 text-xl font-semibold text-slate-800">Documento completato</h2>
            <p className="mt-2 text-sm text-slate-500">Grazie. Il documento è stato archiviato presso la struttura.</p>
          </div>
        ) : (
          <>
            <div className="rounded-xl border bg-white overflow-hidden">
              <div className="border-b px-4 py-3 text-sm font-medium text-slate-700">Anteprima documento</div>
              <iframe src={pdfUrl} title="Documento" className="w-full" style={{ height: "50vh", border: "none" }} />
            </div>

            {data.modalita_firma_digitale && (
              <div className="rounded-xl border bg-white p-6 space-y-4">
                <h2 className="font-semibold text-slate-800">Firma digitale</h2>
                <div>
                  <Label>Luogo</Label>
                  <Input value={luogo} onChange={(e) => setLuogo(e.target.value)} placeholder="Es. Roma" className="mt-1" />
                </div>
                <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50">
                  <SignatureCanvas ref={sigRef} canvasProps={{ className: "w-full h-32" }} />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => sigRef.current?.clear()}>Cancella firma</Button>
                  <Button className="bg-[#00A86B] hover:bg-[#00915d]" onClick={handleFirma} disabled={signing}>
                    {signing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Conferma firma
                  </Button>
                </div>
              </div>
            )}

            {data.modalita_upload_pdf && (
              <div className="rounded-xl border bg-white p-6">
                <h2 className="font-semibold text-slate-800">Carica PDF firmato</h2>
                <p className="mt-1 text-sm text-slate-500">Scarica, stampa, firma e ricarica il documento.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => window.open(pdfUrl, "_blank")}>
                    <Download className="mr-2 h-4 w-4" />Scarica PDF
                  </Button>
                  <label>
                    <Button variant="outline" asChild disabled={uploading}>
                      <span><Upload className="mr-2 h-4 w-4" />{uploading ? "Caricamento…" : "Carica firmato"}</span>
                    </Button>
                    <input type="file" className="hidden" accept=".pdf" onChange={(e) => handleUpload(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
